import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { ControlValueAccessorModel, getProvider } from '../../../Common/Forms/models/control-value-accessor.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';

import { ViDimensionEnum } from '../../enums/vi-dimension.enum';
import { ViIntentEnum } from '../../enums/vi-intent.enum';
import { ViQuestionApi } from '../../../api/visibilityIntelligence/models/vi-question.api.model';


@Component({
    selector: 'agpr-vi-question-details',
    templateUrl: './vi-question-details.component.html',
    styleUrls: ['./vi-question-details.component.scss'],
    providers: [getProvider(ViQuestionDetailsComponent)],
    standalone: false
})
export class ViQuestionDetailsComponent extends ControlValueAccessorModel<ViQuestionApi> {
    readonly buttonTypes = ButtonTypesEnum;

    readonly dimensionOptions: DropdownOptionModel<ViDimensionEnum>[] = Object.values(ViDimensionEnum).map(
        dimension => new DropdownOptionModel(`VisibilityIntelligence.querySetup.dimensions.${dimension}`, dimension)
    );

    readonly intentTypeOptions: DropdownOptionModel<ViIntentEnum>[] = Object.values(ViIntentEnum).map(
        intentType => new DropdownOptionModel(`VisibilityIntelligence.querySetup.reviewQuestion.questionDetails.intentType.${intentType}`, intentType)
    );

    readonly form: FormGroup = new FormGroup({
        dimension: new FormControl<ViDimensionEnum | null>(null),
        question: new FormControl<string>('', { nonNullable: true, validators: [Validators.required], updateOn: 'blur' }),
        expectedAnswerSignals: new FormControl<string[]>([], { nonNullable: true, validators: [Validators.required] }),
        answerSignalDraft: new FormControl<string>('', { nonNullable: true, updateOn: 'blur' }),
        intentType: new FormControl<ViIntentEnum | null>(null)
    });

    @Input() id: string = 'viQuestionDetails';
    @Input() showErrors: boolean = true;

    isBranded: boolean = true;

    constructor() {
        super();
        this.form.valueChanges.subscribe(value => {
            if (!this.value) {
                return;
            }
            this.value.dimension = value.dimension;
            this.value.question = value.question?.trim() ?? '';
            this.value.expectedAnswerSignals = value.expectedAnswerSignals;
            this.value.intentType = value.intentType;
            this.onModelChange(this.value);
        });

        this.form.controls['answerSignalDraft'].valueChanges.subscribe(draft => {
            const trimmed = draft?.trim();
            this.form.controls['answerSignalDraft'].setValue('', { emitEvent: false });
            if (!trimmed) {
                return;
            }
            const signals = this.form.controls['expectedAnswerSignals'];
            signals.setValue([...(signals.value as string[]), trimmed]);
            signals.markAsDirty();
        });
    }

    onIsBrandedChange(isBranded: boolean): void {
        if (!this.value) {
            return;
        }
        this.value.isBranded = isBranded;
        this.onModelChange(this.value);
    }

    removeAnswerSignal(index: number): void {
        const signals = this.form.controls['expectedAnswerSignals'];
        signals.setValue((signals.value as string[]).filter((_, i) => i !== index));
        signals.markAsDirty();
    }

    onAnswerSignalKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter') {
            return;
        }
        // Commit on Enter instead of inserting a newline: updateOn:'blur' only commits the pending
        // value once the field loses focus, so blur it (triggering that commit) then refocus it so
        // the user can keep adding signals without re-clicking into the field.
        event.preventDefault();
        const target = event.target as HTMLElement;
        target.blur();
        requestAnimationFrame(() => target.focus());
    }

    override writeValue(value: ViQuestionApi): void {
        this.value = value;
        this.isBranded = value?.isBranded ?? true;
        this.form.patchValue({
            dimension: value?.dimension ?? null,
            question: value?.question ?? '',
            expectedAnswerSignals: value?.expectedAnswerSignals ?? [],
            answerSignalDraft: '',
            intentType: value?.intentType ?? null
        }, { emitEvent: false });
        this.form.markAsPristine();
    }
}
